import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSaenggibuEventTopics } from '../../api/personalizationApi';
import './SchoolEventTopics.css';

// 생기부 종류 → 보여줄 소재 종류
const KIND_BY_CATEGORY = {
  '창체': new Set(['creative', 'both']),
  '행동발달': new Set(['behavior', 'both']),
};

const SOURCE_LINE = '나이스 학사일정 기준 · 행사 이름은 기재요령에 맞는 일반 표현으로 바꿔 넣어요';

/** 고른 관찰 모습을 활동 내용에 넣을 한 줄로 만든다 (기재요령: 기관명 없음, 일반 표현). 어미는 그대로 둔다. */
export const buildInsertText = (topic, points) => {
  const chosen = (points || []).filter(Boolean).map((p) => String(p).trim().replace(/\.$/, ''));
  if (!topic || chosen.length === 0) return '';
  return `${topic.label}(${topic.date_dot})에서 ${chosen.join(', ')}`;
};

/**
 * 📝 우리 학교 최근 행사에서 소재 고르기 — 창체/자율·행동발달 폼의 활동 내용 위.
 * 학교가 나이스로 연결된 선생님에게만(서버가 아니면 빈 목록) 보이고, 없으면 아무것도 그리지 않는다.
 */
export default function SchoolEventTopics({ currentUser, category, onInsert, onShown }) {
  const [topics, setTopics] = useState([]);
  const [hidden, setHidden] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [checked, setChecked] = useState({});
  const requestIdRef = useRef(0);
  const shownRef = useRef(false);

  useEffect(() => {
    shownRef.current = false;
    // 진행 중이던 이전 요청은 무효화 (로그아웃 뒤 늦게 도착한 응답이 화면을 다시 채우지 않게)
    const requestId = ++requestIdRef.current;
    if (!currentUser) {
      setTopics([]);
      return undefined;
    }
    getSaenggibuEventTopics(currentUser)
      .then((payload) => {
        if (requestId !== requestIdRef.current) return;
        setTopics(Array.isArray(payload?.topics) ? payload.topics : []);
      })
      .catch(() => {
        if (requestId === requestIdRef.current) setTopics([]);
      });
    return () => {
      requestIdRef.current += 1;
    };
  }, [currentUser]);

  const visible = useMemo(() => {
    const allowed = KIND_BY_CATEGORY[category];
    if (!allowed) return [];
    return topics.filter((topic) => allowed.has(topic.kind));
  }, [topics, category]);

  useEffect(() => {
    if (visible.length > 0 && !hidden && !shownRef.current) {
      shownRef.current = true;
      if (typeof onShown === 'function') onShown(visible.length);
    }
  }, [visible, hidden, onShown]);

  // 종류가 바뀌면 펼친 행사·체크를 접는다
  useEffect(() => {
    setActiveId(null);
    setChecked({});
  }, [category]);

  const active = visible.find((topic) => topic.id === activeId) || null;

  const toggleTopic = useCallback((topic) => {
    setActiveId((prev) => (prev === topic.id ? null : topic.id));
    setChecked({});
  }, []);

  const togglePoint = useCallback((point) => {
    setChecked((prev) => ({ ...prev, [point]: !prev[point] }));
  }, []);

  const chosen = useMemo(
    () => (active ? active.points.filter((point) => checked[point]) : []),
    [active, checked],
  );

  const handleInsert = useCallback(() => {
    if (!active || chosen.length === 0) return;
    const text = buildInsertText(active, chosen);
    if (typeof onInsert === 'function') onInsert(text, { topic: active, points: chosen });
    setActiveId(null);
    setChecked({});
  }, [active, chosen, onInsert]);

  if (hidden || visible.length === 0) return null;

  return (
    <section className="school-event-topics" aria-label="우리 학교 최근 행사에서 소재 고르기" data-testid="school-event-topics">
      <div className="school-event-topics__head">
        <span className="school-event-topics__title">
          우리 학교 최근 행사에서 소재 고르기
          <small>학교급·담당 학년 기준</small>
        </span>
        <button type="button" className="school-event-topics__hide" onClick={() => setHidden(true)}>
          숨기기
        </button>
      </div>
      <div className="school-event-topics__row">
        {visible.map((topic) => (
          <button
            key={topic.id}
            type="button"
            className={`school-event-topics__chip${topic.id === activeId ? ' is-active' : ''}`}
            aria-pressed={topic.id === activeId}
            aria-label={`${topic.label} ${topic.dday_label}${topic.dday > 0 ? ' 예정' : ''}`}
            onClick={() => toggleTopic(topic)}
          >
            <span className="school-event-topics__date">{topic.dday > 0 ? topic.dday_label : topic.date.slice(5).replace('-', '/')}</span>
            {topic.label}
            {topic.grades && topic.grades.length > 0 && (
              <span className="school-event-topics__meta">{topic.grades.join('·')}학년</span>
            )}
            {topic.dday > 0 && <span className="school-event-topics__meta">예정</span>}
          </button>
        ))}
      </div>

      {active && (
        <div className="school-event-topics__points" data-testid="school-event-topics-points">
          <div className="school-event-topics__points-head">
            <span>{active.label}에서 자주 관찰되는 모습 — 해당하는 것만 고르세요</span>
            <span>AI 제안</span>
          </div>
          {active.points.map((point) => (
            <label key={point} className="school-event-topics__point">
              <input
                type="checkbox"
                checked={Boolean(checked[point])}
                onChange={() => togglePoint(point)}
              />
              <span>{point}</span>
            </label>
          ))}
          <div className="school-event-topics__foot">
            <span className="school-event-topics__note">{SOURCE_LINE}</span>
            <button
              type="button"
              className="school-event-topics__insert"
              disabled={chosen.length === 0}
              onClick={handleInsert}
            >
              {chosen.length > 0 ? `${chosen.length}개 활동 내용에 넣기` : '고른 것을 활동 내용에 넣기'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
